import { useState, useEffect } from 'react';
import {
  X, Layers, Check, RefreshCw, Sparkles, ExternalLink, Target, Monitor, Download
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api, { API_BASE_URL } from '../../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';

export default function ModernCompetitorDashboard({
  isOpen,
  onClose,
  leadId,
  businessName
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/competitor/${leadId}`);
      setAnalysis(data);
    } catch (err) {
      console.log("No analysis found or error", err);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && leadId) {
      fetchAnalysis();
    }
  }, [isOpen, leadId]);

  const handleTriggerAnalysis = async () => {
    if (!leadId) return;
    setIsRefreshing(true);
    try {
      await api.post(`/competitor/analyze/${leadId}`);
      await fetchAnalysis();
    } catch (err) {
      console.error("Trigger failed", err);
      alert("Failed to analyze competitors.");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isOpen) return null;

  const getPriorityColor = (p) => {
    if (p === "High") return "text-red-400 bg-red-500/10 border border-red-500/20";
    if (p === "Medium") return "text-orange-400 bg-orange-500/10 border border-orange-500/20";
    return "text-green-400 bg-green-500/10 border border-green-500/20";
  };

  const renderTechPill = (techName, confidence) => (
    <div key={techName} className="flex items-center justify-between p-2 bg-slate-900/50 border border-white/[0.08] rounded-lg">
      <span className="text-xs font-semibold text-white">{techName}</span>
      <span className="text-[10px] text-slate-400">{confidence}% Confidence</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 overflow-y-auto">
      <div className="glass-card w-full max-w-5xl max-h-[90vh] min-h-[70vh] overflow-hidden rounded-3xl border border-white/[0.08] shadow-2xl flex flex-col bg-slate-900/90 text-slate-100">

        <div className="p-6 border-b border-white/[0.06] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/40 backdrop-blur-md">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20"><Target size={20} /></span>
              <div>
                <h2 className="text-xl font-bold tracking-tight text-white">AI Competitor Intelligence</h2>
                <p className="text-xs text-slate-400 font-mono">Market analysis for: {businessName}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleTriggerAnalysis} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl transition-all">
              <RefreshCw size={14} className={cn(isRefreshing && "animate-spin")} /> New Deep Scan
            </button>
            <button
              onClick={() => {
                const element = document.getElementById('competitor-report-content');
                if (element) {
                  const opt = {
                    margin: 10,
                    filename: `${businessName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_competitor_report.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                  };
                  html2pdf().set(opt).from(element).save();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-xl transition-all"
            >
              <Download size={14} /> Export PDF
            </button>
            <button onClick={onClose} className="p-2 bg-white/[0.05] hover:bg-white/[0.08] rounded-xl transition-colors text-slate-400">
              <X size={18} />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <RefreshCw size={48} className="text-indigo-400 animate-spin mb-4" />
            <p className="text-slate-400">Loading intelligence report...</p>
          </div>
        )}

        {!loading && !analysis && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <Layers size={48} className="text-slate-600 mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">No Intelligence Report Found</h3>
            <p className="text-sm text-slate-400 mb-6">Click "New Deep Scan" to start gathering competitor intelligence.</p>
          </div>
        )}

        {!loading && analysis && (
          <>
            <div className="px-6 border-b border-white/[0.06] flex gap-4 overflow-x-auto bg-slate-900/10">
              {['overview', 'charts', 'competitors', 'tech'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={cn("py-3 text-xs font-semibold border-b-2 transition-all shrink-0 capitalize",
                    activeTab === tab ? "border-indigo-500 text-white font-bold" : "border-transparent text-slate-400 hover:text-slate-200")}>
                  {tab === 'charts' ? 'Compare Charts' : tab === 'tech' ? 'Tech Stack' : tab}
                </button>
              ))}
            </div>

            <div id="competitor-report-content" className="flex-1 overflow-y-auto p-6 space-y-6">

              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Competitors Analyzed</span>
                      <div className="text-3xl font-extrabold text-white mt-2">{analysis.competitors.length}</div>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Priority Target</span>
                      {analysis.opportunity_score?.priority && (
                        <span className={cn("px-2.5 py-1 rounded-lg text-xs font-bold w-fit mt-2 uppercase tracking-wide", getPriorityColor(analysis.opportunity_score.priority))}>
                          {analysis.opportunity_score.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06]">
                    <h4 className="text-xs font-bold uppercase text-slate-400 mb-4 flex items-center gap-1.5"><Sparkles size={14} className="text-indigo-400" /> Executive Insights</h4>
                    <div className="space-y-4">
                      {analysis.sales_insights && analysis.sales_insights.map((insight, idx) => (
                        <div key={idx} className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-sm text-slate-300 flex items-start gap-2">
                          <Check size={16} className="text-indigo-400 shrink-0 mt-0.5" /> <span>{insight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06]">
                    <h4 className="text-xs font-bold uppercase text-slate-400 mb-4 flex items-center gap-1.5"><Layers size={14} className="text-indigo-400" /> AI SWOT Comparison</h4>
                    <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]">
                      <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-white/[0.05] text-xs font-bold text-slate-400 uppercase tracking-wider">
                          <tr><th className="px-6 py-3">Category</th><th className="px-6 py-3">Prospect</th><th className="px-6 py-3">Competitor (Top)</th></tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.06]">
                          {[
                            { label: "SEO", prospect: analysis.summary?.lead_avg_seo || 40, top: 90 },
                            { label: "Reviews", prospect: analysis.summary?.lead_reviews || 20, top: 95 },
                            { label: "Website", prospect: analysis.summary?.lead_avg_perf || 50, top: 85 },
                            { label: "Trust", prospect: analysis.summary?.lead_avg_trust || 60, top: 92 }
                          ].map((row, idx) => (
                            <tr key={idx} className="hover:bg-white/[0.02]">
                              <td className="px-6 py-4 font-semibold text-white">{row.label}</td>
                              <td className="px-6 py-4">
                                <span className={cn("px-2 py-1 rounded text-xs font-bold", row.prospect > 70 ? "bg-green-500/10 text-green-400" : row.prospect > 40 ? "bg-orange-500/10 text-orange-400" : "bg-red-500/10 text-red-400")}>{Math.round(row.prospect)}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={cn("px-2 py-1 rounded text-xs font-bold", row.top > 70 ? "bg-green-500/10 text-green-400" : row.top > 40 ? "bg-orange-500/10 text-orange-400" : "bg-red-500/10 text-red-400")}>{Math.round(row.top)}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'charts' && (
                <div className="space-y-6">
                  <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/[0.06] h-[400px]">
                    <h4 className="text-sm font-bold text-white mb-6">Competitor Performance Overview</h4>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analysis.competitors}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip cursor={{ fill: '#1e293b' }} contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px' }} />
                        <Legend />
                        <Bar dataKey="ranking_score" name="Overall Rank Score" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="opportunity_score" name="Opportunity Score" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {activeTab === 'tech' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.competitors.map((comp, idx) => (
                      <div key={idx} className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06]">
                        <h4 className="text-sm font-bold text-white mb-1">{comp.name}</h4>
                        <p className="text-[10px] text-slate-400 mb-4 flex items-center gap-1"><Monitor size={10} /> Discovered Technologies</p>
                        <div className="space-y-2">
                          {comp.tech_stack && Object.keys(comp.tech_stack).length > 0 ? (
                            Object.entries(comp.tech_stack).map(([tech, conf]) => renderTechPill(tech, conf))
                          ) : <span className="text-xs text-slate-500">No specific technologies detected.</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'competitors' && (
                <div className="space-y-4">
                  {analysis.competitors.map((comp, idx) => (
                    <div key={idx} className="border border-white/[0.06] rounded-2xl bg-white/[0.02] overflow-hidden flex flex-col md:flex-row">
                      {comp.screenshot && (
                        <div className="md:w-1/3 bg-white/[0.03] shrink-0">
                          <img src={`${API_BASE_URL}${comp.screenshot}`} alt={`${comp.name} website`} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      <div className="p-6 flex-1 flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className="bg-indigo-500 text-white font-black px-2 py-1 rounded text-xs">#{comp.rank || idx + 1}</span>
                            <h4 className="text-lg font-bold text-white">{comp.name}</h4>
                          </div>
                          <a href={comp.website || '#'} target="_blank" rel="noreferrer" className="text-indigo-400 text-xs font-bold hover:underline flex items-center gap-1">
                            {comp.website} <ExternalLink size={10} />
                          </a>
                          <div className="flex items-center gap-4 mt-4">
                            <div className="flex flex-col">
                              <span className="text-[10px] text-slate-500 uppercase font-bold">Rank Score</span>
                              <span className="text-sm font-bold text-indigo-300">{comp.ranking_score || 0}/100</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] text-slate-500 uppercase font-bold">Reviews</span>
                              <span className="text-sm font-bold text-slate-300">{comp.rating} ({comp.reviews_count})</span>
                            </div>
                          </div>
                        </div>
                        <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 rounded">Source: {comp.source || "Google Maps"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
}
